import { createGlobalStyle } from 'styled-components';

import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { resolveTargetNodeId } from './review-utils';
import { useHighlightedFindingStore } from './use-highlighted-finding-store';

import { useNodes } from '../hooks/use-nodes';
import { NODE_CARD_ATTRIBUTE, TRIGGER_ID_ATTRIBUTE } from '../node-types/node-markers';

// Keyed on ReactFlow's node wrapper (`data-id`), so one rule covers every node type
// and re-renders nothing. `outline` over `border` so nothing reflows; the viewport is
// left alone.
export const nodeOutlineCss = ({
  nodeId,
  color,
  triggerId = null,
}: {
  nodeId: string;
  color: string;
  triggerId?: string | null;
}) => {
  const node = `.react-flow__node[data-id="${nodeId}"]`;
  const triggerRow = triggerId ? `[${TRIGGER_ID_ATTRIBUTE}="${triggerId}"]` : null;
  const wrapper = triggerRow ? `${node}:not(:has(${triggerRow}))` : node;
  const unmarkedWrapper = `${wrapper}:not(:has([${NODE_CARD_ATTRIBUTE}]))`;
  const targets = [
    unmarkedWrapper,
    `${wrapper} [${NODE_CARD_ATTRIBUTE}]`,
    ...(triggerRow ? [`${node} ${triggerRow}`] : []),
  ];
  return `
    ${targets.join(',\n    ')} {
      outline-style: solid;
      outline-width: ${pxToRem(2)};
      outline-color: ${color};
      outline-offset: ${pxToRem(4)};
    }
    ${unmarkedWrapper} {
      border-radius: ${pxToRem(12)};
    }
    ${triggerRow ? `${node} ${triggerRow} { outline-offset: ${pxToRem(-2)}; }` : ''}
  `;
};

const NodeOutline = createGlobalStyle<{
  $nodeId: string;
  $color: string;
  $triggerId: string | null;
}>`
  ${(props) =>
    nodeOutlineCss({ nodeId: props.$nodeId, color: props.$color, triggerId: props.$triggerId })}
`;

export type HighlightedNode = {
  nodeId: string;
  color: Color;
  triggerId: string | null;
};

/**
 * The node to outline for the hovered card, or null: nothing hovered, a field
 * finding (no node), or an action no longer on the canvas.
 */
export const useHighlightedNode = (): HighlightedNode | null => {
  const highlighted = useHighlightedFindingStore((state) => state.highlighted);
  const { getValidNodes } = useNodes();

  if (!highlighted) {
    return null;
  }

  const nodeId = resolveTargetNodeId(
    highlighted.target,
    getValidNodes().map((node) => ({ id: node.id, type: node.type })),
  );
  if (!nodeId) {
    return null;
  }
  const { target } = highlighted;
  return {
    nodeId,
    color: highlighted.color,
    triggerId: target.kind === 'trigger' ? target.id : null,
  };
};

export const HighlightedNodeOutline = () => {
  const highlighted = useHighlightedNode();

  if (!highlighted) {
    return null;
  }

  return (
    <NodeOutline
      $nodeId={highlighted.nodeId}
      $color={theme.color[highlighted.color]}
      $triggerId={highlighted.triggerId}
    />
  );
};
